import i18n from "../../language/i18n";
import { IBaseLoadingProps } from "../../properties/LoadingProperties";

// The CatchUpper mark as the loader: the logo's two strokes draw in, the light one catching up
// with the dark one. `variant="wordmark"` shows the full logo with a sheen instead (the app's
// boot screen). CSS lives in loadingStyles (`ld-`), aggregated into libraryStyles.
//
// The mark keeps the logo's own two colours whatever `primaryColor` a call site passes — the
// logo is not recoloured. The one exception is white, which call sites use for a loader sitting
// on a filled button; there the mark turns monochrome white.

// Centerlines traced from public/logos/logo-primary-icon.webp (65×104), stroke width 14.5.
// The viewBox pads the mark to a square so it sits centred in the loader's box.
const MARK_DARK =
  "M12.5 9.5 C26 9.5 43.5 19 44.2 37 L44.5 59 C44.5 66.5 50 70 56.5 69.7";
const MARK_LIGHT =
  "M7.3 33 C14 33 18.8 38 18.8 46 L18.8 62 C19 79 30 91.5 50.5 93.5";
const MARK_STROKE_WIDTH = 14.5;
const BRAND_DARK = "#114859";
const BRAND_LIGHT = "#57C2D3";
// app-web's plain wordmark is logo-primary-2.webp (6176×1640) — the same artwork admin-web's boot
// loader uses as logo-primary.webp; here logo-primary.webp is the mascot lockup.
const WORDMARK_SRC = "/logos/logo-primary-2.webp";
const WORDMARK_ASPECT = 6176 / 1640;
const WORDMARK_HEIGHT_RATIO = 0.42;

const isWhite = (color?: string) =>
  !!color && /^(#fff(fff)?|white)$/i.test(color.trim());

const BaseLoading = ({
  height,
  width,
  size,
  primaryColor,
  decorative,
  variant,
}: IBaseLoadingProps) => {
  let dim: number;

  if (size === "small") {
    dim = 16;
  } else if (size === "medium") {
    dim = 48;
  } else if (size === "large") {
    dim = 72;
  } else if (size === "xlarge") {
    dim = 96;
  } else {
    dim = height || width || 24;
  }

  const margin =
    size === "small"
      ? 4
      : size === "medium"
        ? 12
        : size === "large"
          ? 18
          : size === "xlarge"
            ? 24
            : 0;

  const a11y = decorative
    ? { "aria-hidden": true as const }
    : { role: "status", "aria-label": i18n.t("loading") };

  const white = isWhite(primaryColor);

  // The wordmark is unreadable below large; smaller boxes keep the mark.
  if (variant === "wordmark" && dim >= 72 && !white) {
    const wordHeight = Math.round(dim * WORDMARK_HEIGHT_RATIO);
    return (
      <div
        className="ld-wrap"
        style={{ marginTop: margin, marginBottom: margin }}
        {...a11y}
      >
        <span
          className="ld-word"
          style={{
            width: Math.round(wordHeight * WORDMARK_ASPECT),
            height: wordHeight,
          }}
        >
          <img className="ld-word-dim" src={WORDMARK_SRC} alt="" />
          <img className="ld-word-lit" src={WORDMARK_SRC} alt="" />
        </span>
      </div>
    );
  }

  const darkColor = white ? "#FFFFFF" : BRAND_DARK;
  const lightColor = white ? "rgba(255, 255, 255, 0.6)" : BRAND_LIGHT;

  return (
    <div
      className="ld-wrap"
      style={{ marginTop: margin, marginBottom: margin }}
      {...a11y}
    >
      <svg
        className="ld-mark"
        width={dim}
        height={dim}
        viewBox="-19.5 0 104 104"
        focusable="false"
      >
        <g className="ld-pop">
          <path
            className="ld-stroke ld-stroke-dark"
            d={MARK_DARK}
            stroke={darkColor}
            strokeWidth={MARK_STROKE_WIDTH}
            pathLength={100}
          />
          <path
            className="ld-stroke ld-stroke-light"
            d={MARK_LIGHT}
            stroke={lightColor}
            strokeWidth={MARK_STROKE_WIDTH}
            pathLength={100}
          />
        </g>
      </svg>
    </div>
  );
};

export default BaseLoading;
